import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { meApi, meQueryKeys } from "../../shared/api/me";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

/** T78 — Hồ sơ của tôi. Real /api/v1/me. */
export function MyProfilePage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: meQueryKeys.profile(),
    queryFn: () => meApi.get(),
  });

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.data) {
      setFullName(q.data.fullName ?? "");
      setEmail(q.data.email ?? "");
      setPhone(q.data.phone ?? "");
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      meApi.update({
        fullName: fullName || null,
        email: email || null,
        phone: phone || null,
      }),
    onSuccess: (p) => {
      qc.setQueryData(meQueryKeys.profile(), p);
      setMsg("Đã lưu hồ sơ.");
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const avatar = useMutation({
    mutationFn: (f: File) => meApi.uploadAvatar(f),
    onSuccess: (p) => {
      qc.setQueryData(meQueryKeys.profile(), p);
      setMsg("Đã cập nhật ảnh đại diện.");
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const p = q.data;

  return (
    <div className="font-sans">
      <PageHeader
        title="Hồ sơ của tôi"
        description="Thông tin liên hệ của tài khoản đang đăng nhập."
      />

      {q.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
      {q.isError && (
        <p className="text-sm text-destructive">Không tải được hồ sơ.</p>
      )}

      {p && (
        <>
          <Card className="mb-4">
            <CardContent className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <strong className="text-base">{p.username}</strong>
                <div className="text-[13px] text-muted-foreground">
                  Tạo tài khoản:{" "}
                  {p.createdAt
                    ? new Date(p.createdAt).toLocaleDateString("vi-VN")
                    : "—"}{" "}
                  · Đăng nhập gần nhất:{" "}
                  {p.lastLoginAt
                    ? new Date(p.lastLoginAt).toLocaleString("vi-VN")
                    : "—"}
                </div>
              </div>
              <Badge variant={p.mfaEnrolled ? "success" : "warning"}>
                {p.mfaEnrolled ? "2FA bật" : "2FA tắt"}
              </Badge>
            </CardContent>
          </Card>

          <Card className="max-w-[520px]">
            <CardContent className="p-4">
              <form
                className="grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  setMsg(null);
                  setError(null);
                  save.mutate();
                }}
              >
                <div className="space-y-1.5">
                  <Label htmlFor="fullName">Họ và tên</Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    maxLength={256}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={256}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="phone">Số điện thoại</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={32}
                    placeholder="+8490…"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Ảnh đại diện</Label>
                  <div className="flex items-center gap-3">
                    {p.avatarUrl ? (
                      <img
                        src={p.avatarUrl}
                        alt="Ảnh đại diện"
                        className="h-14 w-14 rounded-full border border-input object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-xl text-muted-foreground">
                        {p.username.slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      disabled={avatar.isPending}
                      className="cursor-pointer file:mr-3 file:cursor-pointer file:border-0 file:bg-transparent file:text-sm file:font-medium"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          setMsg(null);
                          setError(null);
                          avatar.mutate(f);
                        }
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {avatar.isPending
                      ? "Đang tải ảnh…"
                      : "Ảnh PNG/JPG, tối đa 200MB."}
                  </p>
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}
                {msg && (
                  <p className="text-sm font-medium text-emerald-600">{msg}</p>
                )}

                <div className="flex justify-end">
                  <Button type="submit" disabled={save.isPending}>
                    {save.isPending ? "Đang lưu…" : "Lưu thay đổi"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
